import React from "react";
import { Box, Typography, CardContent, Divider, Grid } from "@mui/material";
import { StyledCard } from "../../common/StyledCard";
import { getEntityMeta } from "../../../helpers/dashboardHelpers";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { InventoryItemStatsDto } from "../../../shared/dtos/inventoryItem/InventoryItemStatsDto";
import { formatCurrency } from "../../../utils/currencyUtils";

export interface ExtendedInventoryItemStatsDto extends InventoryItemStatsDto {
  itemsBelowReorderLevel?: number;
  itemsOutOfStock?: number;
}

interface InventoryOverviewWidgetProps {
  data: ExtendedInventoryItemStatsDto;
}

const InventoryOverviewWidget: React.FC<InventoryOverviewWidgetProps> = ({
  data,
}) => {
  const meta = getEntityMeta("INVENTORY_ITEM");
  const Icon = meta.icon;

  return (
    <StyledCard sx={{ height: "100%" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: `${meta.color}15`,
              color: meta.color,
              display: "flex",
              mr: 2,
            }}
          >
            <Icon />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {meta.title}
          </Typography>
        </Box>

        <Box sx={{ textAlign: "center", my: 3 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Total Stock Value
          </Typography>
          <Typography
            variant="h4"
            sx={{ fontWeight: "bold", color: "secondary.main" }}
          >
            {formatCurrency(data.totalInventoryValue)}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: "warning.main",
                mb: 0.5,
              }}
            >
              <WarningAmberIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Low Stock
              </Typography>
            </Box>
            <Typography variant="h6">
              {data.itemsBelowReorderLevel ?? 0}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: "error.main",
                mb: 0.5,
              }}
            >
              <ErrorOutlineIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Out of Stock
              </Typography>
            </Box>
            <Typography variant="h6">{data.itemsOutOfStock ?? 0}</Typography>
          </Grid>
        </Grid>
      </CardContent>
    </StyledCard>
  );
};

export default InventoryOverviewWidget;
